import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const PanoramaAssembler = () => {
  const [sceneName, setSceneName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [uploadedPanorama, setUploadedPanorama] = useState(null);
  const [assembledImage, setAssembledImage] = useState(null);
  const [panoramas, setPanoramas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPanoramas();
  }, []);

  const fetchPanoramas = async () => {
    try {
      const baseURL = window.location.hostname === 'localhost'
        ? 'http://localhost:5000'
        : 'https://satoshi-homepage-59650ad0a3ce.herokuapp.com';

      const response = await fetch(`${baseURL}/api/admin/panoramas`, {
        headers: {
          'adminEmail': localStorage.getItem('adminEmail'),
          'adminToken': localStorage.getItem('adminToken')
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPanoramas(data);
      }
    } catch (error) {
      console.error('Error fetching panoramas:', error);
    }
  };

  const handlePanoramaUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedPanorama(file);
        setAssembledImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const savePanorama = async () => {
    if (!assembledImage || !sceneName || !displayName) {
      setMessage('❌ Please enter scene name, display name, and upload an image');
      return;
    }

    setLoading(true);
    setMessage('💾 Saving panorama...');

    try {
      const baseURL = window.location.hostname === 'localhost'
        ? 'http://localhost:5000'
        : 'https://satoshi-homepage-59650ad0a3ce.herokuapp.com';

      const response = await fetch(`${baseURL}/api/admin/panorama/assemble`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'adminEmail': localStorage.getItem('adminEmail'),
          'adminToken': localStorage.getItem('adminToken')
        },
        body: JSON.stringify({
          sceneName: sceneName.toLowerCase().replace(/\s+/g, '-'),
          assembledImageData: assembledImage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save panorama');
      }

      setMessage('✅ Panorama saved successfully!');
      fetchPanoramas();

      // Reset form
      setTimeout(() => {
        resetForm();
      }, 2000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage('❌ Error saving panorama');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSceneName('');
    setDisplayName('');
    setDescription('');
    setUploadedPanorama(null);
    setAssembledImage(null);
    setMessage('');
  };

  const downloadAssembledImage = () => {
    if (!assembledImage) return;

    const link = document.createElement('a');
    link.download = `${sceneName || 'panorama'}-assembled.jpg`;
    link.href = assembledImage;
    link.click();
  };

  return (
    <div>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginBottom: '1.5rem', color: '#333', fontSize: '1.75rem' }}>
          🎨 Panorama Uploader
        </h2>
        <p style={{ color: '#666', marginBottom: '2rem', fontSize: '1rem' }}>
          Upload panoramas you've assembled manually using Corel Draw, Photoshop, or your drawing tablet.
        </p>

        {/* Scene Info */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#555' }}>
                Scene Name (URL-friendly)
              </label>
              <input
                type="text"
                value={sceneName}
                onChange={(e) => setSceneName(e.target.value)}
                placeholder="e.g., megayacht-islands"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#555' }}>
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Megayacht Island Vista"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#555' }}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this scene..."
              rows={2}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>
        </div>

        {/* Panorama Upload Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#555' }}>
            📤 Upload Your Assembled Panorama
          </h3>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Upload a panorama you've assembled and blended manually using Corel Draw, Photoshop, or your drawing tablet.
            Recommended size: 5376 x 2048 pixels (or larger).
          </p>

            {assembledImage ? (
              <div style={{
                border: '2px solid #4caf50',
                borderRadius: '8px',
                padding: '1rem',
                backgroundColor: '#f0f8f0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div style={{ color: '#4caf50', fontWeight: '600' }}>
                    ✓ Panorama Uploaded: {uploadedPanorama?.name}
                  </div>
                  <button
                    onClick={() => {
                      setUploadedPanorama(null);
                      setAssembledImage(null);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Remove
                  </button>
                </div>
                <img
                  src={assembledImage}
                  alt="Uploaded panorama"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '4px'
                  }}
                />
              </div>
            ) : (
              <label style={{
                display: 'block',
                border: '3px dashed #ccc',
                borderRadius: '8px',
                padding: '3rem',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: '#fafafa',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.backgroundColor = '#f5f7ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#ccc';
                e.currentTarget.style.backgroundColor = '#fafafa';
              }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePanoramaUpload(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
                <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                  Click to Upload Panorama
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                  Supports JPG, PNG (Max 10MB)
                </div>
              </label>
            )}
        </div>

        {/* Action Buttons */}
        {assembledImage && (
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={downloadAssembledImage}
              style={{
                flex: 1,
                padding: '1rem',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ⬇️ Download Image
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={savePanorama}
              disabled={loading}
              style={{
                flex: 1,
                padding: '1rem',
                backgroundColor: loading ? '#999' : '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '💾 Saving...' : '💾 Save to Database'}
            </motion.button>
          </div>
        )}

        {/* Message */}
        {message && (
          <div style={{
            padding: '1rem',
            backgroundColor: message.includes('❌') ? '#fee' : '#e8f5e8',
            color: message.includes('❌') ? '#c00' : '#2e7d32',
            borderRadius: '8px',
            fontWeight: '600'
          }}>
            {message}
          </div>
        )}

      </div>

      {/* Existing Panoramas */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginBottom: '1.5rem', color: '#333' }}>Saved Panoramas ({panoramas.length})</h2>
        {panoramas.length === 0 ? (
          <p style={{ color: '#666' }}>No panoramas created yet. Create your first one above!</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {panoramas.map((pano) => (
              <div
                key={pano._id}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '1rem',
                  backgroundColor: '#fafafa'
                }}
              >
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#333' }}>
                  {pano.displayName}
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                  {pano.name}
                </p>
                <p style={{ fontSize: '0.8rem', color: '#999' }}>
                  Created: {new Date(pano.createdAt).toLocaleDateString()}
                </p>
                {pano.assembledImageUrl && (
                  <img
                    src={pano.assembledImageUrl}
                    alt={pano.displayName}
                    style={{
                      width: '100%',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PanoramaAssembler;
